import { useCallback, useEffect, useState } from 'react';

import {
  ctaCopy, detectPlatform, installMode, isDismissed, markDismissed, readEnv,
  type BeforeInstallPromptEvent, type InstallMode, type Platform,
} from '@/pwa/install';
import { Icon, type IconName } from '@/components/ui/Icon';

/**
 * "Add to Home Screen" CTA.
 *
 * Shows nothing at all unless an install is genuinely possible — see
 * `pwa/install.ts` for the platform matrix. On Android and Windows this is one
 * tap. **On iOS it opens instructions instead**, because Apple exposes no
 * install API and a button that silently does nothing is worse than no button.
 */
export function InstallPrompt() {
  const [mode, setMode] = useState<InstallMode>('unsupported');
  const [platform, setPlatform] = useState<Platform>('other');
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showSteps, setShowSteps] = useState(false);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    const env = readEnv();
    setPlatform(detectPlatform(env));
    setMode(installMode(env));
    setHidden(isDismissed());

    const onPrompt = (e: Event) => {
      /* Chromium shows its own mini-infobar otherwise, which we cannot style and
         which competes with this card. */
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setMode(installMode({ ...readEnv(), hasPrompt: true }));
    };
    const onInstalled = () => setMode('installed');

    globalThis.addEventListener('beforeinstallprompt', onPrompt);
    globalThis.addEventListener('appinstalled', onInstalled);
    return () => {
      globalThis.removeEventListener('beforeinstallprompt', onPrompt);
      globalThis.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (mode === 'instructions') { setShowSteps(true); return; }
    if (!deferred) return;

    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    /* The event is single-use — Chromium will not let us call prompt() twice. */
    setDeferred(null);
    if (outcome === 'accepted') setMode('installed');
    else { markDismissed(); setHidden(true); }
  }, [deferred, mode]);

  const dismiss = useCallback(() => { markDismissed(); setHidden(true); }, []);

  const copy = ctaCopy(mode, platform);
  if (!copy || hidden) return null;

  return (
    <>
      <div className="mx-3 mb-2 flex items-center gap-2.5 rounded-md border
                      border-[rgba(255,196,140,.3)] bg-[linear-gradient(#3A2718,var(--cave-floor))]
                      px-3 py-2.5 shadow-[0_3px_0_-1px_rgba(0,0,0,.5)]">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg
                         bg-black/40 text-torch-core" aria-hidden>
          <Icon name="pick" size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-pixel text-[8px] tracking-wide text-torch-core">{copy.title}</p>
          <p className="mt-0.5 text-[10px] leading-snug text-ink-dim">{copy.body}</p>
        </div>
        <button
          type="button"
          onClick={() => void install()}
          className="shrink-0 rounded-sm border border-[rgba(255,220,170,.5)]
                     bg-[linear-gradient(#FFA945,#E0761C)] px-3 py-1.5
                     font-pixel text-[7.5px] text-[#2A1503]
                     shadow-[0_3px_0_-1px_#8C440C] active:translate-y-0.5"
        >
          {copy.action}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 px-1 text-xs text-ink-dim"
          aria-label="Not now"
        >
          <Icon name="close" size={13} />
        </button>
      </div>

      {showSteps && (
        <div className="fixed inset-0 z-[60] grid place-items-end bg-black/75 p-4 backdrop-blur-sm"
             role="dialog" aria-modal="true" aria-label="Add to Home Screen">
          <button type="button" className="absolute inset-0 cursor-default"
                  onClick={() => setShowSteps(false)} aria-label="Close" tabIndex={-1} />
          <div className="relative w-full rounded-lg border border-ui-lit
                          bg-[linear-gradient(#2A1B10,#1A110A)] p-4">
            <h3 className="font-pixel text-[9px] text-torch-core">ADD TO HOME SCREEN</h3>
            <p className="mt-2 text-[11px] leading-relaxed text-ink-dim">
              iOS does not allow apps to install themselves, so this takes three taps
              in Safari:
            </p>
            <ol className="mt-3 space-y-2.5">
              {([
                ['share', 'Tap the Share button in the Safari toolbar'],
                ['plus', 'Scroll down and choose “Add to Home Screen”'],
                ['check', 'Tap “Add” — ShweTap joins your other apps'],
              ] as [IconName, string][]).map(([icon, text], i) => (
                <li key={text} className="flex items-start gap-2.5">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full
                                   bg-[rgba(255,179,71,.15)] font-pixel text-[7px]
                                   text-torch-core">{i + 1}</span>
                  <span className="leading-none text-torch-core" aria-hidden>
                    <Icon name={icon} size={18} />
                  </span>
                  <span className="flex-1 text-[11.5px] leading-snug text-ink">{text}</span>
                </li>
              ))}
            </ol>
            <button
              type="button"
              onClick={() => { setShowSteps(false); dismiss(); }}
              className="mt-4 w-full rounded-sm border border-ui-lit bg-cave-floor py-2
                         font-pixel text-[7.5px] text-ink"
            >
              GOT IT
            </button>
          </div>
        </div>
      )}
    </>
  );
}
